import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import paymentGatewayConfigService from '../services/paymentGatewayConfigService';
import useConfirm from '../hooks/useConfirm';
import './PaymentGatewayConfigs.css';

const GATEWAY_INFO = {
  mercadopago: {
    name: 'Mercado Pago',
    description: 'Receba pagamentos via PIX, boleto e cartao de credito no Brasil e America Latina.',
    color: '#009ee3',
  },
  stripe: {
    name: 'Stripe',
    description: 'Receba pagamentos internacionais via cartao de credito, debito e mais de 135 moedas.',
    color: '#635bff',
  },
  oasyfy: {
    name: 'Oasyfy',
    description: 'Receba pagamentos via PIX, boleto e cartao de credito com taxas competitivas.',
    color: '#2ac473',
  },
};

const MercadoPagoLogo = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
    <rect width="48" height="48" rx="12" fill="#009ee3"/>
    <path d="M24 12c-5.4 0-10.2 3-12.6 7.6-.4.8.1 1.4.9 1.4h23.4c.8 0 1.3-.6.9-1.4C34.2 15 29.4 12 24 12z" fill="#fff"/>
    <path d="M11.4 23c-.8 0-1.3.6-.9 1.4C12.9 29 17.7 32 23.1 32h1.8c5.4 0 10.2-3 12.6-7.6.4-.8-.1-1.4-.9-1.4H11.4z" fill="#fff" opacity=".7"/>
    <ellipse cx="24" cy="27" rx="4" ry="3.5" fill="#fff"/>
  </svg>
);

const StripeLogo = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
    <rect width="48" height="48" rx="12" fill="#635bff"/>
    <path d="M22.3 19.7c0-1.1.9-1.5 2.4-1.5 2.1 0 4.8.7 6.9 1.8V14c-2.3-.9-4.6-1.3-6.9-1.3-5.6 0-9.4 2.9-9.4 7.8 0 7.6 10.5 6.4 10.5 9.7 0 1.3-1.1 1.7-2.7 1.7-2.3 0-5.3-.9-7.6-2.2v6.1c2.6 1.1 5.2 1.6 7.6 1.6 5.8 0 9.7-2.8 9.7-7.8-.1-8.2-10.5-6.7-10.5-9.9z" fill="#fff"/>
  </svg>
);

const OasyfyLogo = () => (
  <svg viewBox="0 0 48 48" width="48" height="48" fill="none">
    <rect width="48" height="48" rx="12" fill="#2ac473"/>
    <path d="M24 10c-7.7 0-14 6.3-14 14s6.3 14 14 14 14-6.3 14-14-6.3-14-14-14zm0 24c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10-4.5 10-10 10z" fill="#fff"/>
    <path d="M24 18c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="#fff"/>
  </svg>
);

const PaymentGatewayConfigs = () => {
  const { confirm, DialogComponent } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [botId, setBotId] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connectingGateway, setConnectingGateway] = useState(null);
  const [showManualConfig, setShowManualConfig] = useState(false);

  // Modal states (manual config)
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState('mercadopago');
  const [selectedEnvironment, setSelectedEnvironment] = useState('test');
  const [apiStatus, setApiStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [formData, setFormData] = useState({
    gateway: 'mercadopago',
    environment: 'test',
    access_token: '',
    public_key: '',
    client_id: '',
    client_secret: '',
    secret_key: '',
    webhook_secret: '',
    webhook_url: '',
    is_active: false
  });

  const loadConfigs = useCallback(async (id) => {
    try {
      setLoading(true);
      const data = await paymentGatewayConfigService.getConfigs(id);
      setConfigs(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar configuracoes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedBotId = localStorage.getItem('selectedBotId');
    if (!storedBotId) {
      setError('Bot nao selecionado. Por favor, selecione um bot primeiro.');
      setLoading(false);
      return;
    }
    setBotId(storedBotId);
    loadConfigs(storedBotId);
  }, [loadConfigs]);

  // Handle OAuth callback query params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('error');

    if (connected) {
      const gatewayName = GATEWAY_INFO[connected]?.name || connected;
      setSuccess(`${gatewayName} conectado com sucesso!`);
      setSearchParams({}, { replace: true });
      if (botId) loadConfigs(botId);
      setTimeout(() => setSuccess(''), 5000);
    } else if (oauthError) {
      setError(`Erro ao conectar: ${oauthError}`);
      setSearchParams({}, { replace: true });
      setTimeout(() => setError(''), 8000);
    }
  }, [searchParams, setSearchParams, botId, loadConfigs]);

  const getConfigForGateway = (gateway, environment) => {
    return configs.find(c => c.gateway === gateway && c.environment === environment);
  };

  const getOAuthConfig = (gateway) => {
    return configs.find(c => c.gateway === gateway && c.connection_method === 'oauth');
  };

  const isGatewayConnected = (gateway) => {
    const config = getOAuthConfig(gateway);
    return config && config.connection_method === 'oauth' && config.connected_account_id;
  };

  const hasAnyConfig = (gateway) => {
    return configs.some(c => c.gateway === gateway && c.is_active);
  };

  // ─── OAuth Actions ─────────────────────────────────────────────────

  const handleOAuthConnect = async (gateway) => {
    if (!botId) return;
    setConnectingGateway(gateway);
    try {
      const data = await paymentGatewayConfigService.getOAuthAuthUrl(botId, gateway);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao iniciar conexao OAuth');
      setTimeout(() => setError(''), 5000);
      setConnectingGateway(null);
    }
  };

  const handleOAuthDisconnect = async (gateway) => {
    const config = getOAuthConfig(gateway);
    if (!config) return;

    const gatewayName = GATEWAY_INFO[gateway]?.name || gateway;
    const confirmed = await confirm({
      message: `Tem certeza que deseja desconectar o ${gatewayName}? Os pagamentos via este gateway deixarao de funcionar.`,
      type: 'warning',
    });

    if (!confirmed) return;

    try {
      await paymentGatewayConfigService.disconnectOAuth(config.id);
      setSuccess(`${gatewayName} desconectado com sucesso.`);
      loadConfigs(botId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desconectar gateway');
      setTimeout(() => setError(''), 5000);
    }
  };

  // ─── Manual Config Actions ─────────────────────────────────────────

  const handleOpenModal = (gateway, environment) => {
    setSelectedGateway(gateway);
    setSelectedEnvironment(environment);

    const existingConfig = getConfigForGateway(gateway, environment);

    if (existingConfig) {
      setEditingConfig(existingConfig);
      setFormData({
        gateway: existingConfig.gateway || gateway,
        environment: existingConfig.environment || environment,
        access_token: existingConfig.access_token || existingConfig.api_key || '',
        public_key: existingConfig.public_key || '',
        client_id: existingConfig.client_id || '',
        client_secret: existingConfig.client_secret || '',
        secret_key: existingConfig.secret_key || existingConfig.api_secret || '',
        webhook_secret: existingConfig.webhook_secret || '',
        webhook_url: existingConfig.webhook_url || '',
        is_active: existingConfig.is_active !== undefined ? existingConfig.is_active : (existingConfig.active !== undefined ? existingConfig.active : false)
      });
    } else {
      setEditingConfig(null);
      setFormData({
        gateway,
        environment,
        access_token: '',
        public_key: '',
        client_id: '',
        client_secret: '',
        secret_key: '',
        webhook_secret: '',
        webhook_url: '',
        is_active: false
      });
    }

    setShowModal(true);
  };

  const handleTestConnection = async () => {
    if (!editingConfig) {
      setTestResult({ status: 'error', message: 'Salve a configuracao primeiro para testar a conexao.' });
      return;
    }
    setTestingConnection(true);
    setTestResult(null);
    try {
      const status = await paymentGatewayConfigService.checkApiStatus(botId, selectedGateway, selectedEnvironment);
      setTestResult(status);
    } catch (err) {
      setTestResult({
        status: 'error',
        message: err.response?.data?.message || 'Erro ao testar conexao'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setTestResult(null);
    setFormData({
      gateway: 'mercadopago',
      environment: 'test',
      access_token: '',
      public_key: '',
      client_id: '',
      client_secret: '',
      secret_key: '',
      webhook_secret: '',
      webhook_url: '',
      is_active: false
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const configToSave = {
        gateway: formData.gateway || selectedGateway,
        environment: formData.environment || selectedEnvironment,
        bot_id: botId,
        is_active: formData.is_active !== undefined ? formData.is_active : false,
        webhook_url: formData.webhook_url || null,
      };

      if (formData.gateway === 'mercadopago' || selectedGateway === 'mercadopago') {
        configToSave.access_token = formData.access_token || '';
        configToSave.public_key = formData.public_key || '';
        configToSave.client_id = formData.client_id || '';
        configToSave.client_secret = formData.client_secret || '';
        configToSave.webhook_secret = formData.webhook_secret || null;
      } else if (formData.gateway === 'oasyfy' || selectedGateway === 'oasyfy') {
        configToSave.public_key = formData.public_key || '';
        configToSave.secret_key = formData.secret_key || '';
        configToSave.webhook_url = 'https://api.easypagamentos.com/api/payments/webhook/oasyfy';
      } else if (formData.gateway === 'stripe' || selectedGateway === 'stripe') {
        configToSave.secret_key = formData.secret_key || '';
        configToSave.public_key = formData.public_key || '';
        configToSave.webhook_secret = formData.webhook_secret || null;
      }

      if (editingConfig) {
        await paymentGatewayConfigService.updateConfig(editingConfig.id, configToSave);
        setSuccess('Configuracao atualizada com sucesso!');
      } else {
        await paymentGatewayConfigService.createOrUpdateConfig(configToSave);
        setSuccess('Configuracao salva com sucesso!');
      }

      handleCloseModal();
      loadConfigs(botId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.keys(errors).map(key => {
          return `${key}: ${Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key]}`;
        }).join('\n');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.error || err.message || 'Erro ao salvar configuracao');
      }
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      message: 'Tem certeza que deseja excluir esta configuracao?',
      type: 'warning',
    });

    if (!confirmed) return;

    try {
      await paymentGatewayConfigService.deleteConfig(id);
      setSuccess('Configuracao excluida com sucesso!');
      loadConfigs(botId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir configuracao');
    }
  };

  const maskToken = (token) => {
    if (!token) return '';
    if (token.length <= 8) return token;
    return token.substring(0, 4) + '........' + token.substring(token.length - 4);
  };

  const handleCheckApiStatus = async (gateway, environment) => {
    setLoadingStatus(true);
    setShowStatusModal(true);
    setSelectedGateway(gateway);
    setSelectedEnvironment(environment);
    setApiStatus(null);

    try {
      const status = await paymentGatewayConfigService.checkApiStatus(botId, gateway, environment);
      setApiStatus(status);
    } catch (err) {
      setApiStatus({
        status: 'error',
        message: 'Erro ao verificar status',
        details: err.response?.data?.message || err.message || 'Erro desconhecido'
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setApiStatus(null);
  };

  // ─── Render ────────────────────────────────────────────────────────

  if (loading && !botId) {
    return (
      <Layout>
        <div className="pgc-page">
          <div className="pgc-loading">Carregando...</div>
        </div>
      </Layout>
    );
  }

  if (!botId) {
    return (
      <Layout>
        <div className="pgc-page">
          <div className="pgc-empty">
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const renderGatewayCard = (gateway) => {
    const info = GATEWAY_INFO[gateway];
    const oauthConfig = getOAuthConfig(gateway);
    const connected = isGatewayConnected(gateway);
    const hasManual = hasAnyConfig(gateway) && !connected;
    const isManualOnly = gateway === 'oasyfy';
    const Logo = gateway === 'mercadopago' ? MercadoPagoLogo : gateway === 'oasyfy' ? OasyfyLogo : StripeLogo;

    return (
      <div className={`pgc-card ${connected ? 'pgc-card--connected' : ''}`} key={gateway}>
        <div className="pgc-card__header">
          <div className="pgc-card__logo">
            <Logo />
          </div>
          <div className="pgc-card__title">
            <h3>{info.name}</h3>
            <p>{info.description}</p>
          </div>
        </div>

        <div className="pgc-card__status">
          {connected ? (
            <div className="pgc-status pgc-status--connected">
              <span className="pgc-status__dot"></span>
              Conectado
            </div>
          ) : hasManual ? (
            <div className="pgc-status pgc-status--manual">
              <span className="pgc-status__dot"></span>
              Configurado manualmente
            </div>
          ) : (
            <div className="pgc-status pgc-status--disconnected">
              <span className="pgc-status__dot"></span>
              Nao conectado
            </div>
          )}
        </div>

        {connected && oauthConfig && (
          <div className="pgc-card__details">
            <div className="pgc-detail">
              <span className="pgc-detail__label">Conta</span>
              <span className="pgc-detail__value">{oauthConfig.connected_account_id}</span>
            </div>
            <div className="pgc-detail">
              <span className="pgc-detail__label">Ambiente</span>
              <span className="pgc-detail__value">{oauthConfig.environment === 'production' ? 'Producao' : 'Teste'}</span>
            </div>
            {oauthConfig.oauth_metadata?.connected_at && (
              <div className="pgc-detail">
                <span className="pgc-detail__label">Conectado em</span>
                <span className="pgc-detail__value">
                  {new Date(oauthConfig.oauth_metadata.connected_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            )}
            {oauthConfig.webhook_url && (
              <div className="pgc-detail">
                <span className="pgc-detail__label">Webhook</span>
                <span className="pgc-detail__value pgc-detail__value--mono">
                  {oauthConfig.webhook_url}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="pgc-card__actions">
          {connected ? (
            <>
              <button
                className="pgc-btn pgc-btn--outline"
                onClick={() => handleCheckApiStatus(gateway, oauthConfig?.environment || 'production')}
              >
                Verificar Status
              </button>
              <button
                className="pgc-btn pgc-btn--danger-outline"
                onClick={() => handleOAuthDisconnect(gateway)}
              >
                Desconectar
              </button>
            </>
          ) : isManualOnly ? (
            <>
              <button
                className="pgc-btn pgc-btn--oauth"
                style={{ '--gateway-color': info.color }}
                onClick={() => handleOpenModal(gateway, 'production')}
              >
                Configurar {info.name}
              </button>
              {hasManual && (
                <button
                  className="pgc-btn pgc-btn--outline"
                  onClick={() => handleCheckApiStatus(gateway, 'production')}
                >
                  Verificar Status
                </button>
              )}
            </>
          ) : (
            <button
              className="pgc-btn pgc-btn--oauth"
              style={{ '--gateway-color': info.color }}
              onClick={() => handleOAuthConnect(gateway)}
              disabled={connectingGateway === gateway}
            >
              {connectingGateway === gateway ? (
                <span className="pgc-btn__loading">Conectando...</span>
              ) : (
                <>Conectar com {info.name}</>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <DialogComponent />
      <div className="pgc-page">
        <div className="pgc-header">
          <h1>Gateways de Pagamento</h1>
          <p>Conecte seus gateways de pagamento para comecar a receber.</p>
        </div>

        {error && <div className="pgc-alert pgc-alert--error">{error}</div>}
        {success && <div className="pgc-alert pgc-alert--success">{success}</div>}

        {loading ? (
          <div className="pgc-loading">Carregando configuracoes...</div>
        ) : (
          <>
            {/* OAuth Cards Grid */}
            <div className="pgc-grid">
              {renderGatewayCard('mercadopago')}
              {renderGatewayCard('stripe')}
              {renderGatewayCard('oasyfy')}
            </div>

            {/* Manual Config Section */}
            <div className="pgc-manual">
              <button
                className="pgc-manual__toggle"
                onClick={() => setShowManualConfig(!showManualConfig)}
              >
                <span className={`pgc-manual__arrow ${showManualConfig ? 'pgc-manual__arrow--open' : ''}`}>
                  &#9654;
                </span>
                Configuracao Manual (Avancado)
              </button>

              {showManualConfig && (
                <div className="pgc-manual__content">
                  {/* Mercado Pago Section */}
                  <div className="gateway-section">
                    <div className="gateway-header">
                      <div className="gateway-title-wrapper">
                        <h2>Mercado Pago (PIX)</h2>
                        <div className="status-api-buttons">
                          <button
                            className="btn-status-api"
                            onClick={() => handleCheckApiStatus('mercadopago', 'test')}
                          >
                            Status API (Teste)
                          </button>
                          <button
                            className="btn-status-api"
                            onClick={() => handleCheckApiStatus('mercadopago', 'production')}
                          >
                            Status API (Producao)
                          </button>
                        </div>
                      </div>
                      <div className="gateway-badges">
                        <button
                          className={`badge ${getConfigForGateway('mercadopago', 'test')?.is_active ? 'active' : ''}`}
                          onClick={() => handleOpenModal('mercadopago', 'test')}
                        >
                          {getConfigForGateway('mercadopago', 'test') ? 'Teste' : '+ Teste'}
                        </button>
                        <button
                          className={`badge ${getConfigForGateway('mercadopago', 'production')?.is_active ? 'active' : ''}`}
                          onClick={() => handleOpenModal('mercadopago', 'production')}
                        >
                          {getConfigForGateway('mercadopago', 'production') ? 'Producao' : '+ Producao'}
                        </button>
                      </div>
                    </div>

                    <div className="gateway-configs-grid">
                      {['test', 'production'].map((env) => {
                        const config = getConfigForGateway('mercadopago', env);
                        return (
                          <div className="config-card" key={env}>
                            <div className="config-card-header">
                              <h3>{env === 'test' ? 'Ambiente de Teste' : 'Ambiente de Producao'}</h3>
                              {config?.is_active && <span className="badge-active">Ativo</span>}
                            </div>
                            {config ? (
                              <div className="config-card-content">
                                <p><strong>Access Token:</strong> {maskToken(config.access_token || config.api_key || '')}</p>
                                <p><strong>Public Key:</strong> {config.public_key ? maskToken(config.public_key) : 'Nao configurado'}</p>
                                <p><strong>Client ID:</strong> {config.client_id || 'Nao configurado'}</p>
                                <p><strong>Webhook URL:</strong> {config.webhook_url || 'Nao configurado'}</p>
                                {config.connection_method === 'oauth' && (
                                  <p><strong>Metodo:</strong> OAuth (ID: {config.connected_account_id})</p>
                                )}
                                <div className="config-card-actions">
                                  <button className="btn btn-edit" onClick={() => handleOpenModal('mercadopago', env)}>Editar</button>
                                  <button className="btn btn-delete" onClick={() => handleDelete(config.id)}>Excluir</button>
                                </div>
                              </div>
                            ) : (
                              <div className="config-card-content">
                                <p className="empty-config">Nenhuma configuracao</p>
                                <button className="btn btn-primary" onClick={() => handleOpenModal('mercadopago', env)}>Configurar</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Oasyfy Section */}
                  <div className="gateway-section">
                    <div className="gateway-header">
                      <div className="gateway-title-wrapper">
                        <h2>Oasyfy (PIX / Boleto / Cartao)</h2>
                        <div className="status-api-buttons">
                          <button
                            className="btn-status-api"
                            onClick={() => handleCheckApiStatus('oasyfy', 'production')}
                          >
                            Status API
                          </button>
                        </div>
                      </div>
                      <div className="gateway-badges">
                        <button
                          className={`badge ${getConfigForGateway('oasyfy', 'production')?.is_active ? 'active' : ''}`}
                          onClick={() => handleOpenModal('oasyfy', 'production')}
                        >
                          {getConfigForGateway('oasyfy', 'production') ? 'Producao' : '+ Producao'}
                        </button>
                      </div>
                    </div>

                    <div className="gateway-configs-grid">
                      {['production'].map((env) => {
                        const config = getConfigForGateway('oasyfy', env);
                        return (
                          <div className="config-card" key={env}>
                            <div className="config-card-header">
                              <h3>Ambiente de Producao</h3>
                              {config?.is_active && <span className="badge-active">Ativo</span>}
                            </div>
                            {config ? (
                              <div className="config-card-content">
                                <p><strong>Public Key:</strong> {maskToken(config.public_key || '')}</p>
                                <p><strong>Secret Key:</strong> {maskToken(config.secret_key || config.api_secret || '')}</p>
                                <p><strong>Webhook URL:</strong> {config.webhook_url || 'Nao configurado'}</p>
                                <div className="config-card-actions">
                                  <button className="btn btn-edit" onClick={() => handleOpenModal('oasyfy', env)}>Editar</button>
                                  <button className="btn btn-delete" onClick={() => handleDelete(config.id)}>Excluir</button>
                                </div>
                              </div>
                            ) : (
                              <div className="config-card-content">
                                <p className="empty-config">Nenhuma configuracao</p>
                                <button className="btn btn-primary" onClick={() => handleOpenModal('oasyfy', env)}>Configurar</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stripe Section */}
                  <div className="gateway-section">
                    <div className="gateway-header">
                      <div className="gateway-title-wrapper">
                        <h2>Stripe (Cartao de Credito)</h2>
                        <div className="status-api-buttons">
                          <button
                            className="btn-status-api"
                            onClick={() => handleCheckApiStatus('stripe', 'test')}
                          >
                            Status API (Teste)
                          </button>
                          <button
                            className="btn-status-api"
                            onClick={() => handleCheckApiStatus('stripe', 'production')}
                          >
                            Status API (Producao)
                          </button>
                        </div>
                      </div>
                      <div className="gateway-badges">
                        <button
                          className={`badge ${getConfigForGateway('stripe', 'test')?.is_active ? 'active' : ''}`}
                          onClick={() => handleOpenModal('stripe', 'test')}
                        >
                          {getConfigForGateway('stripe', 'test') ? 'Teste' : '+ Teste'}
                        </button>
                        <button
                          className={`badge ${getConfigForGateway('stripe', 'production')?.is_active ? 'active' : ''}`}
                          onClick={() => handleOpenModal('stripe', 'production')}
                        >
                          {getConfigForGateway('stripe', 'production') ? 'Producao' : '+ Producao'}
                        </button>
                      </div>
                    </div>

                    <div className="gateway-configs-grid">
                      {['test', 'production'].map((env) => {
                        const config = getConfigForGateway('stripe', env);
                        return (
                          <div className="config-card" key={env}>
                            <div className="config-card-header">
                              <h3>{env === 'test' ? 'Ambiente de Teste' : 'Ambiente de Producao'}</h3>
                              {config?.is_active && <span className="badge-active">Ativo</span>}
                            </div>
                            {config ? (
                              <div className="config-card-content">
                                <p><strong>Secret Key:</strong> {maskToken(config.secret_key || config.api_secret || '')}</p>
                                <p><strong>Public Key:</strong> {maskToken(config.public_key) || 'Nao configurado'}</p>
                                <p><strong>Webhook Secret:</strong> {maskToken(config.webhook_secret) || 'Nao configurado'}</p>
                                {config.connection_method === 'oauth' && (
                                  <p><strong>Metodo:</strong> OAuth (ID: {config.connected_account_id})</p>
                                )}
                                <div className="config-card-actions">
                                  <button className="btn btn-edit" onClick={() => handleOpenModal('stripe', env)}>Editar</button>
                                  <button className="btn btn-delete" onClick={() => handleDelete(config.id)}>Excluir</button>
                                </div>
                              </div>
                            ) : (
                              <div className="config-card-content">
                                <p className="empty-config">Nenhuma configuracao</p>
                                <button className="btn btn-primary" onClick={() => handleOpenModal('stripe', env)}>Configurar</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Manual Config Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {editingConfig ? 'Editar' : 'Nova'} Configuracao - {GATEWAY_INFO[selectedGateway]?.name || selectedGateway} ({selectedEnvironment === 'test' ? 'Teste' : 'Producao'})
                </h2>
                <button className="modal-close" onClick={handleCloseModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                {selectedGateway === 'mercadopago' ? (
                  <>
                    <div className="form-group">
                      <label>Access Token *</label>
                      <input type="password" name="access_token" value={formData.access_token} onChange={handleChange} required placeholder="APP_USR-..." />
                      <small>Token de acesso do Mercado Pago (obrigatorio)</small>
                    </div>
                    <div className="form-group">
                      <label>Public Key</label>
                      <input type="text" name="public_key" value={formData.public_key} onChange={handleChange} placeholder="APP_USR-..." />
                      <small>Chave publica do Mercado Pago (usada no frontend)</small>
                    </div>
                    <div className="form-group">
                      <label>Client ID</label>
                      <input type="text" name="client_id" value={formData.client_id} onChange={handleChange} placeholder="1234567890123456" />
                      <small>ID unico da sua integracao no Mercado Pago</small>
                    </div>
                    <div className="form-group">
                      <label>Client Secret</label>
                      <input type="password" name="client_secret" value={formData.client_secret} onChange={handleChange} placeholder="Chave secreta do cliente" />
                      <small>Chave secreta usada em alguns plugins para gerar pagamentos</small>
                    </div>
                    <div className="form-group">
                      <label>Webhook URL</label>
                      <input type="url" name="webhook_url" value={formData.webhook_url} onChange={handleChange} placeholder="https://seu-dominio.com/api/payments/webhook/mercadopago" />
                      <small>URL para receber notificacoes do Mercado Pago</small>
                    </div>
                    <div className="form-group">
                      <label>Webhook Secret</label>
                      <input type="password" name="webhook_secret" value={formData.webhook_secret} onChange={handleChange} placeholder="Chave secreta para validar webhooks" />
                      <small>Chave secreta para validar a autenticidade dos webhooks (opcional)</small>
                    </div>
                  </>
                ) : selectedGateway === 'oasyfy' ? (
                  <>
                    <div className="form-group">
                      <label>Public Key (x-public-key) *</label>
                      <input type="text" name="public_key" value={formData.public_key} onChange={handleChange} required placeholder="Sua chave publica da Oasyfy" />
                      <small>Chave publica gerada no painel da Oasyfy (Integracoes &gt; API)</small>
                    </div>
                    <div className="form-group">
                      <label>Secret Key (x-secret-key) *</label>
                      <input type="password" name="secret_key" value={formData.secret_key} onChange={handleChange} required placeholder="Sua chave secreta da Oasyfy" />
                      <small>Chave secreta gerada no painel da Oasyfy (nao sera exibida novamente)</small>
                    </div>
                    <div className="form-group" style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '4px' }}>Configuracao do Webhook na Oasyfy</label>
                      <small style={{ display: 'block', marginBottom: '8px' }}>Copie a URL abaixo e configure no painel da Oasyfy em <strong>Configuracoes &gt; Webhooks</strong>:</small>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="text" readOnly value="https://api.easypagamentos.com/api/payments/webhook/oasyfy" style={{ flex: 1, background: '#fff', cursor: 'text', fontSize: '13px' }} onClick={(e) => { e.target.select(); navigator.clipboard.writeText(e.target.value).catch(() => {}); }} />
                        <button type="button" className="btn btn-edit" style={{ whiteSpace: 'nowrap', padding: '6px 12px' }} onClick={() => { navigator.clipboard.writeText('https://api.easypagamentos.com/api/payments/webhook/oasyfy').then(() => { setSuccess('URL copiada!'); setTimeout(() => setSuccess(''), 2000); }).catch(() => {}); }}>Copiar</button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Secret Key *</label>
                      <input type="password" name="secret_key" value={formData.secret_key} onChange={handleChange} required placeholder="sk_test_..." />
                      <small>Chave secreta do Stripe</small>
                    </div>
                    <div className="form-group">
                      <label>Public Key *</label>
                      <input type="text" name="public_key" value={formData.public_key} onChange={handleChange} required placeholder="pk_test_..." />
                      <small>Chave publica do Stripe (para uso no frontend) - Obrigatoria</small>
                    </div>
                    <div className="form-group">
                      <label>Webhook Secret</label>
                      <input type="password" name="webhook_secret" value={formData.webhook_secret} onChange={handleChange} placeholder="whsec_..." />
                      <small>Secret do webhook do Stripe</small>
                    </div>
                    <div className="form-group">
                      <label>Webhook URL</label>
                      <input type="url" name="webhook_url" value={formData.webhook_url} onChange={handleChange} placeholder="https://seu-dominio.com/api/payments/webhook/stripe" />
                      <small>URL para receber notificacoes do Stripe</small>
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>
                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                    Ativar esta configuracao
                  </label>
                  <small>Quando ativado, esta configuracao sera usada para processar pagamentos</small>
                </div>
                {testResult && (
                  <div className={`alert ${testResult.status === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '16px' }}>
                    {testResult.status === 'success' ? 'OK ' : 'ERRO '}{testResult.message}
                  </div>
                )}
                <div className="modal-footer">
                  {editingConfig && (
                    <button type="button" onClick={handleTestConnection} className="btn btn-edit" disabled={testingConnection}>
                      {testingConnection ? 'Testando...' : 'Testar Conexao'}
                    </button>
                  )}
                  <button type="button" onClick={handleCloseModal} className="btn btn-cancel">Cancelar</button>
                  <button type="submit" className="btn btn-primary">{editingConfig ? 'Atualizar' : 'Salvar'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Status API Modal */}
        {showStatusModal && (
          <div className="modal-overlay" onClick={handleCloseStatusModal}>
            <div className="modal-content status-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  Status da API - {GATEWAY_INFO[selectedGateway]?.name || selectedGateway} ({selectedEnvironment === 'test' ? 'Teste' : 'Producao'})
                </h2>
                <button className="modal-close" onClick={handleCloseStatusModal}>&times;</button>
              </div>
              <div className="status-modal-content">
                {loadingStatus ? (
                  <div className="status-loading"><p>Verificando status da API...</p></div>
                ) : apiStatus ? (
                  <div className={`status-result status-${apiStatus.status}`}>
                    <div className="status-icon">
                      {apiStatus.status === 'success' && 'OK'}
                      {apiStatus.status === 'error' && 'ERRO'}
                      {apiStatus.status === 'warning' && 'AVISO'}
                    </div>
                    <h3 className="status-message">{apiStatus.message}</h3>
                    {apiStatus.details && (
                      <div className="status-details">
                        {typeof apiStatus.details === 'string' ? (
                          <p>{apiStatus.details}</p>
                        ) : (
                          <div className="status-details-object">
                            {Object.entries(apiStatus.details).map(([key, value]) => {
                              if (key === 'payment_methods_available' && Array.isArray(value)) {
                                return (
                                  <div key={key} className="status-detail-item payment-methods">
                                    <strong>Metodos de Pagamento Habilitados:</strong>
                                    <div className="payment-methods-list">
                                      {value.map((method, index) => (
                                        <span key={index} className={`payment-method-badge ${method === 'pix' ? 'pix-badge' : ''}`}>
                                          {method.toUpperCase()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }
                              if (key === 'pix_enabled') {
                                const pixStatus = value === true ? 'Habilitado' : value === false ? 'Nao Habilitado' : 'Indeterminado';
                                const pixClass = value === true ? 'pix-enabled' : value === false ? 'pix-disabled' : 'pix-indeterminate';
                                return (
                                  <div key={key} className={`status-detail-item ${pixClass}`}>
                                    <strong>PIX:</strong>
                                    <span className={`pix-status ${pixClass}`}>
                                      {pixStatus}
                                      {value === false && (
                                        <span className="pix-warning"> (Verifique se ha uma chave PIX cadastrada na conta)</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div key={key} className="status-detail-item">
                                  <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {String(value)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {apiStatus.timestamp && (
                      <div className="status-timestamp">
                        <small>Verificado em: {new Date(apiStatus.timestamp).toLocaleString('pt-BR')}</small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="status-error"><p>Nenhum status disponivel</p></div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={handleCloseStatusModal} className="btn btn-primary">Fechar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentGatewayConfigs;
